/**
 * `/login/recovery` — the recovery-code sign-in path (routing-and-state.md §1;
 * auth-and-authz.md §1.6: "user enters one of their pre-generated codes if they lose
 * TOTP only"). Collects email + password + one recovery code and calls the store's
 * `signInWithRecovery`.
 *
 * The backend consumes the `recovery_code` at `/auth/login` (single-use, Argon2id) and
 * mints a session, so every `SignInResult.outcome` is routed here: a successful consume
 * lands on /firms (the ADR-0022 B10 firm-picker; or TOTP-enrolment), a rejected code surfaces the error, and the
 * defensive `totp_required` (which should not occur once the code is consumed) shows a
 * clear message rather than silently no-op'ing.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useId, useState, type FormEvent, type JSX } from 'react';

import { AuthCard, FormError, PasswordField, SubmitButton, TextField } from '../components/ui.tsx';
import { getErrorMessage } from '../lib/api.ts';
import { carriedRedirect, useAfterSignIn } from '../router/redirect.ts';
import { useAuth } from '../store/auth-store.ts';

export function RecoveryScreen(): JSX.Element {
  const navigate = useNavigate();
  const errorId = useId();
  const afterSignIn = useAfterSignIn();
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  const carry = carriedRedirect(redirect);
  const signInWithRecovery = useAuth((s) => s.signInWithRecovery);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signInWithRecovery(
        email.trim().toLowerCase(),
        password,
        recoveryCode.trim(),
      );
      switch (result.outcome) {
        case 'authenticated':
          // Identity signed in → the firm picker (single-membership identities auto-select
          // there), or back to the invitation being accepted.
          await afterSignIn(redirect);
          return;
        case 'totp_enrolment_required':
          await navigate({ to: '/login/totp-enrol', search: carry });
          return;
        case 'force_password_change':
          // Mirror login-screen.tsx: a breached deferred-HIBP password (no session).
          await navigate({ to: '/forgot-password', search: carry });
          return;
        case 'totp_required':
          // Defensive: the backend consumes the code, so this should not occur — but
          // never silently no-op (the Batch-D dead-end this task closes).
          setError('That recovery code was not accepted. Check the code and try again.');
          return;
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Sign in with a recovery code"
      subtitle="Use one of the codes you saved when you set up two-factor authentication. Each code works once."
    >
      <form
        onSubmit={handleSubmit}
        aria-label="Sign in with a recovery code"
        style={{ display: 'grid', gap: 12 }}
      >
        <TextField
          label="Work email"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="username"
        />
        <PasswordField
          label="Password"
          name="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          {...(error ? { describedBy: errorId } : {})}
        />
        <TextField
          label="Recovery code"
          name="recovery_code"
          value={recoveryCode}
          onChange={setRecoveryCode}
          autoComplete="off"
          placeholder="xxxx-xxxx-xxxx"
        />
        <FormError message={error} id={errorId} />
        <SubmitButton disabled={busy || email === '' || password === '' || recoveryCode === ''}>
          {busy ? 'Verifying…' : 'Sign in'}
        </SubmitButton>
        <Link to="/login" search={carry} style={{ fontSize: 13 }}>
          Back to sign in
        </Link>
      </form>
    </AuthCard>
  );
}
